// eslint-disable-next-line @typescript-eslint/no-empty-function
import * as Sentry from '@sentry/nextjs';
import { Organization, SortType, Unit } from '@/types/api';
import { t } from 'i18next';
import { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { copyClipBoardText } from '@/utils/index';
import process from 'process';
import { ProfileCardQueryKey } from '@/hooks/useDeepLinkHandler';
import {
  EVENT_BANNER_LIST,
  JIRA_ACCOUNT_URL,
  SET_TOKEN_URL,
} from '@/constant/apiList';

export const emptyFunction = () => {
  // intentionally empty
};

export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const imgUrlChk = (url?: string | null) => {
  // [참고] 클라우드플레어 이미지 리사이즈 처리 가능(URL 으로 다양한 옵션을 부여)
  // https://developers.cloudflare.com/images/transform-images/transform-via-url/#fit
  if (!url) return '';

  return url.includes('https') || url.includes('http') ? url : `https:${url}`;
};

/** Cloudflare Image Resizing 기본 옵션(가로 1200px, 품질 89%). */
export const CLOUDFLARE_RESIZE_DEFAULT_OPTIONS =
  'width=1200,quality=89,format=auto,fit=scale-down';

/**
 * Cloudflare Image Resizing 경유 URL 로 변환한다.
 * 결과 형태: https://[원본도메인]/cdn-cgi/image/[옵션]/[경로]
 * FNF 도메인 이미지가 아니거나 URL 파싱에 실패하면 원본을 그대로 돌려준다.
 * https://developers.cloudflare.com/images/transform-images/transform-via-url/#fit
 */
export const getCloudflareResizedImageUrl = (
  originalUrl: string,
  options: string = CLOUDFLARE_RESIZE_DEFAULT_OPTIONS,
) => {
  if (!originalUrl || !originalUrl.includes('fnf.co.kr')) return originalUrl;

  try {
    const urlObj = new URL(originalUrl);

    return `https://${urlObj.host}/cdn-cgi/image/${options}${urlObj.pathname}${urlObj.search}`;
  } catch (e) {
    return originalUrl;
  }
};

/**
 * 화면에서 이미 정상 흐름으로 처리하는 4xx — Sentry 로 보내지 않는다.
 * 홈에 들어올 때마다 불려 조직 오류 할당량의 대부분을 썼다(2026-10, 14일 6만 건 · 할당량 초과로 앱 오류까지 버려짐).
 * - jira/account-id 403: Jira 계정이 없는 사용자
 * - event-banners/me 403: 이벤트 배너 대상이 아닌 사용자
 * - notificationCenter/tokens/my/create 400: 이미 등록됐거나 지원하지 않는 브라우저의 푸시 토큰
 */
const EXPECTED_CLIENT_ERRORS: { path: string; status: number }[] = [
  { path: JIRA_ACCOUNT_URL, status: 403 },
  { path: EVENT_BANNER_LIST, status: 403 },
  { path: SET_TOKEN_URL, status: 400 },
];

const isExpectedClientError = (error: any) => {
  const status = error?.response?.status;
  const path = String(error?.config?.url ?? '').split('?')[0];
  return EXPECTED_CLIENT_ERRORS.some(
    (e) => e.status === status && path.endsWith(e.path),
  );
};

export const sendSentry = (error: any, tokenType: string) => {
  if (isExpectedClientError(error)) return;
  const { url, method } = error.config;
  Sentry.setExtra('TokenType', tokenType);
  Sentry.setExtra('url', url);
  Sentry.setExtra('method', method);
  Sentry.captureException(error);
};

export const findDataByOrgCode = (
  data: Organization,
  orgCode: string,
): Organization | null => {
  if (data.orgCode === orgCode) {
    return data;
  } else if (data.children) {
    for (let i = 0; i < data.children.length; i++) {
      const result = findDataByOrgCode(data.children[i], orgCode);
      if (result) {
        return result;
      }
    }
  }
  return null; // orgCode를 찾지 못한 경우
};

export const isDepartment = (data: Organization, orgCode: string): boolean => {
  if (data.orgCode === orgCode && data.children.length === 0) {
    return true;
  } else if (data.children) {
    for (let i = 0; i < data.children.length; i++) {
      const result = isDepartment(data.children[i], orgCode);
      if (result) {
        return result;
      }
    }
  }
  return false;
};

export const sendBadgeCount = ({
  tabName,
  badgeCount,
}: {
  tabName: string;
  badgeCount: number;
}) => {
  const sendData = JSON.stringify({
    actionType: 'BADGE_UPDATE',
    tabName: tabName,
    badgeCount: badgeCount,
  });
  window?.ReactNativeWebView?.postMessage(sendData);
};

// TODO 확인 필요) utils > diffTodayText 로 대체 가능한지
export const strDate = (
  t: (key: string) => string,
  dateData?: Date | string,
) => {
  if (!dateData) {
    return '-';
  }
  const date = typeof dateData === 'string' ? new Date(dateData) : dateData;
  const now = new Date();

  const clearTime = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const diffInSeconds =
    (clearTime(now).getTime() - clearTime(date).getTime()) / 1000;

  const secondsInMinute = 60;
  const secondsInHour = 60 * secondsInMinute;
  const secondsInDay = 24 * secondsInHour;
  const secondsInMonth = 30 * secondsInDay;
  const secondsInYear = 12 * secondsInMonth;

  if (clearTime(now).getTime() === clearTime(date).getTime()) {
    return t('오늘');
  } else if (diffInSeconds < 31 * secondsInDay) {
    const days = Math.floor(diffInSeconds / secondsInDay);
    return `${days}${t('일 전')}`;
  } else if (diffInSeconds < secondsInYear) {
    const months = Math.floor(diffInSeconds / secondsInMonth);
    return `${months}${t('개월 전')}`;
  } else {
    const years = Math.floor(diffInSeconds / secondsInYear);
    return `${years}${t('년 전')}`;
  }
};

export const rnPermissionDeny = (errorCode?: number) => {
  if (window.isRNWebview) {
    const sendData = JSON.stringify({
      actionType: 'PERMISSION_DENIED',
      errorCode,
    });
    window?.ReactNativeWebView?.postMessage(sendData);
  }
};

export const tagToColor = (tag: string) => {
  switch (tag) {
    case 'HR팀용':
    case '근태':
    case '채용':
    case '신청':
      return {
        color: '#545E40',
        borderColor: 'rgba(131, 186, 15, 0.20)',
        background:
          'linear-gradient(0deg, rgba(131, 186, 15, 0.06) 0%, rgba(131, 186, 15, 0.06) 100%), #FFF',
      };
    case '발주':
    case '매장':
    case '거래처 신규/변경':
      return {
        color: '#6D445D',
        borderColor: 'rgba(217, 81, 163, 0.20)',
        background:
          'linear-gradient(0deg, rgba(217, 81, 163, 0.06) 0%, rgba(217, 81, 163, 0.06) 100%), #FFF',
      };
    case '보안':
    case '업무':
      return {
        color: 'rgba(74, 55, 115, 1)',
        borderColor: 'rgba(105, 75, 168, 0.20)',
        background:
          'linear-gradient(0deg, rgba(105, 75, 168, 0.06) 0%, rgba(105, 75, 168, 0.06) 100%), #FFF',
      };
    case '정산':
    case 'EAS':
      return {
        color: 'rgba(94, 73, 42, 1)',
        borderColor: 'rgba(213, 135, 19, 0.20)',
        background:
          'linear-gradient(0deg, rgba(213, 135, 19, 0.06) 0%, rgba(213, 135, 19, 0.06) 100%), #FFF',
      };
    case 'meeting':
      return {
        color: '#C77AC4',
        borderColor: '#C77AC4',
        background: 'white',
      };
    case 'new':
    case 'note':
      return {
        color: 'black',
        borderColor: '#E7E9EC',
        background: 'white',
      };
    case 'approval':
      return {
        color: '#06c',
        borderColor: '#06C',
        background:
          'linear-gradient(0deg, rgba(0, 102, 204, 0.08) 0%, rgba(0, 102, 204, 0.08) 100%), #FFF',
      };
    default:
      return {
        color: '#4D5157',
        borderColor: 'rgba(172, 172, 172, 0.20)',
        background:
          'linear-gradient(0deg, rgba(172, 172, 172, 0.06) 0%, rgba(172, 172, 172, 0.06) 100%), #FFF',
      };
  }
};

export const strStatus = (
  t: (key: string, options?: { context?: string }) => string,
  status: string,
) => {
  switch (status) {
    case 'R': //반려
    case 'D': //삭제
      return {
        title: t('반려', { context: 'status' }),
        color: '#F55060',
        borderColor: 'rgba(245, 80, 96, 0.24)',
        backgroundColor: 'rgba(245, 80, 96, 0.12)',
      };
    case 'C':
      return {
        title: t('완료'),
        color: '#43B16F',
        borderColor: 'rgba(67, 177, 111, 0.24)',
        backgroundColor: 'rgba(67, 177, 111, 0.12)',
      };
    case 'H':
      return {
        title: t('보류', { context: 'status' }),
        color: '#FEAE00',
        borderColor: 'rgba(254, 174, 0, 0.24)',
        backgroundColor: 'rgba(254, 174, 0, 0.12)',
      };
    case 'S':
      return {
        title: t('임시저장', { context: 'status' }),
        color: '#000000',
        borderColor: 'rgba(231, 233, 236, 1)',
        backgroundColor: 'rgba(231, 233, 236, 0.32)',
      };
    case 'X': //취소 (디자인은 반려와 동일한 색상 사용)
      return {
        title: t('취소', { context: 'status' }),
        color: '#F55060',
        borderColor: 'rgba(245, 80, 96, 0.24)',
        backgroundColor: 'rgba(245, 80, 96, 0.12)',
      };
    case 'P':
    default:
      return {
        title: t('진행중'),
        color: '#436FB1',
        borderColor: 'rgba(67, 111, 177, 0.24)',
        backgroundColor: 'rgba(67, 111, 177, 0.12)',
      };
  }
};

const NO_DIMS_URL = 'static-dashff.fnf.co.kr';

export const resizeItemImage = (url?: string) => {
  return url
    ? url.includes(NO_DIMS_URL)
      ? url
      : url + '/dims/resize/400'
    : '/images/markup.svg';
};

export const resizeImage = (url?: string) => {
  return url
    ? url.includes(NO_DIMS_URL)
      ? url
      : url + '/dims/resize/400'
    : '/images/image-error-gray.svg';
};

export const saleFormatString = (
  amount: number | string | null | undefined,
) => {
  if (amount === null || amount === undefined) return '-';
  return Math.round(
    typeof amount === 'string' ? parseInt(amount) / 1000 : amount / 1000,
  ).toLocaleString();
};

export const salesRatioString = (
  amount: number | string | null | undefined,
) => {
  if (amount === null || amount === undefined) return '-';
  const num = typeof amount === 'string' ? Number(amount) : amount;
  return num.toLocaleString('ko-KR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

export const saleFormatNumber = (amount: number | string) => {
  return Math.floor(
    typeof amount === 'string' ? parseInt(amount) / 1000 : amount / 1000,
  );
};

export const monthToString = (arg: string) => {
  return t(`${arg}월`);
};

export enum RnMessage {
  LOG_OUT = 'LOG_OUT',
  SET_SALES_CURRENCY = 'SET_SALES_CURRENCY',
  SET_SALES_DEFAULT_DATA = 'SET_SALES_DEFAULT_DATA',
  SET_SALES_INITIALS = 'SET_SALES_INITIALS',
  CHANGE_DEVELOPER_MODE = 'CHANGE_DEVELOPER_MODE',
  GET_VERSION_INFO = 'GET_VERSION_INFO',
  GET_APP_CONFIG = 'GET_APP_CONFIG',
  RESTART = `RESTART`,
  GET_TOKEN_INFO = 'GET_TOKEN_INFO',
  GET_MS_NEW_TOKEN = 'GET_MS_NEW_TOKEN',
  // 앱이 웹뷰를 닫고 이전 화면으로 복귀. data 로 `toast: { message }` 동반 가능
  BACK = 'BACK',
  // 웹뷰가 아닌 외부 브라우저로 열기. data 로 `url` 동반 필요
  OPEN_APP = 'OPEN_APP',
  GET_BLUETOOTH_STATE = 'GET_BLUETOOTH_STATE',
  OPEN_CENTER_PASS = 'OPEN_CENTER_PASS',
  // 앱 안에서 모달(웹뷰 화면)로 열기. data 로 `tabName`, `url` 동반 필요
  MODAL = 'MODAL',
}

// 앱(React Native 웹뷰) 여부. SSR 에서는 항상 false.
export const isRnWebView = () =>
  typeof window !== 'undefined' && !!window.ReactNativeWebView;

export const sendToRN = (message: RnMessage, data?: any) => {
  switch (message) {
    default:
      window?.ReactNativeWebView?.postMessage(
        JSON.stringify({ actionType: message.toString(), ...data }),
      );
      break;
  }
};

// 포탈 밖 주소를 연다. 앱(RN 웹뷰)에서는 웹뷰 안에서 열면 뒤로 가기가 막히므로
// 외부 브라우저로 넘기고, 웹에서는 새 탭으로 띄운다.
export const openExternalUrl = (url: string) => {
  if (isRnWebView()) {
    sendToRN(RnMessage.OPEN_APP, { url });
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
};

export type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

export const setAuthorizationHeader = (
  config: RetryableRequestConfig,
  token: string,
): void => {
  config._retry = true;
  if (config.headers) {
    const headers = config.headers as any;
    if (typeof headers.set === 'function') {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${token}`;
    }
  } else {
    config.headers = {
      Authorization: `Bearer ${token}`,
    } as AxiosRequestHeaders;
  }
};

// RN으로부터 토큰을 요청하고 응답을 기다리는 Promise 생성
export const requestRNToken = (message: RnMessage): Promise<void> => {
  // 이미 대기 중인 Promise가 있으면 재사용
  if (window.rnTokenPromise) {
    return window.rnTokenPromise;
  }

  // 새 Promise 생성
  const promise = new Promise<void>((resolve) => {
    window.rnTokenResolve = resolve;
  });

  window.rnTokenPromise = promise;

  // RN에 토큰 요청
  sendToRN(message);

  return promise;
};

// RN 토큰 응답 완료 처리 (Promise resolve)
export const resolveRNTokenPromise = () => {
  if (window.rnTokenResolve) {
    window.rnTokenResolve();
    window.rnTokenPromise = null;
    window.rnTokenResolve = undefined;
  }
};

export const hoursAgo = (dateInput: string) => {
  // 입력받은 시간을 Date 객체로 변환
  const pastDate = new Date(dateInput);
  // 현재 시간
  const currentDate = new Date();

  // 두 시간의 차이를 밀리초로 계산
  const diff = currentDate.getTime() - pastDate.getTime();
  // 밀리초를 시간으로 변환 (1시간 = 3600000 밀리초)
  const hours = diff / 3600000;

  // 소수점 아래를 버림하여 정수로 표현
  const roundedHours = Math.floor(hours);

  // 결과 문자열 생성
  if (roundedHours <= 0) {
    return 'Updated Just Now';
  } else {
    return `Updated ${roundedHours} Hours Ago`;
  }
};

// 소속(division) 정렬
export const division = (isDesc: boolean) => {
  const dir = isDesc ? 'desc' : 'asc';
  return [
    `detail.divisionName.ko,${dir}`,
    `detail.departmentName,${dir}`,
    'detail.isLeader,desc',
    'detail.isActingTeamLeader,desc',
    'detail.isPartLeader,desc',
    'detail.positionCode,asc',
    'detail.monthsInPosition,desc',
    'detail.overallCareerMonths,desc',
    'detail.name.ko,asc',
    'detail.email,asc',
  ].join(',');
};

// 직급/직책(positionCode) 정렬
export const positionCode = (isDesc: boolean) => {
  const dir = isDesc ? 'desc' : 'asc';
  const positionDir = isDesc ? 'asc' : 'desc';
  return [
    // 이미지 기준: 팀/파트 리더 라벨은 isDesc에 맞춰 변화
    `detail.isLeader,${dir}`,
    `detail.isActingTeamLeader,${dir}`,
    `detail.isPartLeader,${dir}`,
    `detail.positionCode,${positionDir}`,
    `detail.monthsInPosition,${dir}`,
    `detail.overallCareerMonths,${dir}`,
    'detail.divisionName.ko,asc',
    'detail.departmentName,asc',
    'detail.name.ko,asc',
    'detail.email,asc',
  ].join(',');
};

// 이름(name) 정렬
export const name = (isDesc: boolean) => {
  const dir = isDesc ? 'desc' : 'asc';
  return [
    `detail.name.ko,${dir}`,
    'detail.isLeader,desc',
    'detail.isActingTeamLeader,desc',
    'detail.isPartLeader,desc',
    'detail.positionCode,asc',
    'detail.monthsInPosition,desc',
    'detail.overallCareerMonths,desc',
    'detail.divisionName.ko,asc',
    'detail.departmentName,asc',
    'detail.email,asc',
  ].join(',');
};

// 이메일(email) 정렬
export const email = (isDesc: boolean) => {
  const dir = isDesc ? 'desc' : 'asc';
  return [
    `detail.email,${dir}`,
    'detail.isLeader,desc',
    'detail.isActingTeamLeader,desc',
    'detail.isPartLeader,desc',
    'detail.positionCode,asc',
    'detail.monthsInPosition,desc',
    'detail.overallCareerMonths,desc',
    'detail.divisionName.ko,asc',
    'detail.departmentName,asc',
    'detail.name.ko,asc',
  ].join(',');
};

// 최종 선택 스위처
export const sortFilter = (sortType: SortType, isDesc: boolean) => {
  switch (sortType) {
    case SortType.divisionName:
      return division(isDesc);
    case SortType.name:
      return name(isDesc);
    case SortType.positionCode:
      return positionCode(isDesc);
    case SortType.email:
      return email(isDesc);
  }
};

export const formatAudioTime2 = (time: number) => {
  if (!time) return '00:00:00';
  const totalSeconds = Math.floor(time / 1000); // ms → sec 변환
  const hours = Math.floor(totalSeconds / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};

export const formatToHHMMSS = (duration: number): string => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor(duration % 60);

  const pad = (num: number) => String(num).padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

export const numToSalesWon = (amount: number) =>
  Math.round(amount / 1000).toLocaleString();

// 현지화 토글 값 선택 헬퍼
// currency=true → 원화 값, currency=false → 현지화 값(백엔드가 추가한 ...Local 필드).
// 현지화 필드가 없으면 원화 값으로 폴백해 렌더 깨짐을 방지한다.
export const byCurrency = <T>(
  currency: boolean,
  krw: T,
  local: T | undefined | null,
): T => (currency ? krw : local ?? krw);

export const currencySymbol = (unit: Unit) => {
  switch (unit) {
    case 'KRW':
      return '₩';
    case 'USD':
      return '$';
    case 'CNY':
      return '¥';
    case 'JPY':
      return '¥';
    case 'EUR':
      return '€';
    case 'HKD':
      return 'HKD';
    case 'NTD':
      return 'NT$';
    case 'MOP':
      return 'MOP$';
    default:
      return unit;
  }
};

export const copyProfileCardLink = async (passportId?: string) => {
  if (!passportId) return;
  return await copyClipBoardText(
    `${process.env.NEXT_PUBLIC_ORIGIN_URL}?${ProfileCardQueryKey}=${passportId}`,
  );
};

export const openSalesModal = (
  url: string,
  router?: { push: (url: string) => void },
) => {
  if (window?.isRNWebview) {
    const sendData = JSON.stringify({
      actionType: 'MODAL',
      tabName: 'sales',
      url: `${process.env.NEXT_PUBLIC_ORIGIN_URL}${url}`,
    });
    window?.ReactNativeWebView?.postMessage(sendData);
  } else {
    router?.push(url);
  }
};
